import i18n from 'i18next';
import { notify } from '@koob/ui';
import { getFolder } from 'api/folders';

/**
 * Active Client Folder store slice.
 *
 * Single source of truth for the folder the TO is currently working on.
 * Every part of the app (Hotels, Experiences, Trip Designer, sidebar card)
 * reads the active folder from here.
 */
export const createActiveFolderModel = (set, get) => ({
  // The currently active client folder, or null when none is selected.
  activeFolder: null,
  // True while refreshFolder() is re-fetching the active folder.
  isFolderRefreshing: false,
  // Browse folders modal, opened from the sidebar and from every control that
  // needs a client folder.
  browseFoldersOpen: false,
  // 'designTrip' when opened from Design a trip: picking a folder then opens
  // the trip wizard.
  browseFoldersIntent: null,

  setBrowseFoldersOpen: (open, intent = null) =>
    set(
      open
        ? { browseFoldersOpen: true, browseFoldersIntent: intent }
        : { browseFoldersOpen: false },
      false,
      'setBrowseFoldersOpen',
    ),

  // Set the active folder (e.g. when a row/card is clicked in the list).
  // ponytail: snack lives here, not at the 3 call sites (list, browse modal,
  // sidebar) — notify/i18n.t are module singletons, no hook needed.
  // An archived folder is read-only (KD-3842): it never becomes the active
  // folder, so no hotel, experience or trip can be added to it.
  openFolder: (folder, messageKey = 'opened') => {
    if (folder?.archivedAt) {
      return;
    }
    set({ activeFolder: folder }, false, 'openFolder');
    if (folder) {
      notify(
        i18n.t(`folders:activeFolder.${messageKey}`, { name: folder.name }),
        { type: 'info' },
      );
    }
  },

  // Clear the active folder.
  closeFolder: () => {
    const name = get().activeFolder?.name;
    set({ activeFolder: null }, false, 'closeFolder');
    if (name) {
      notify(i18n.t('folders:activeFolder.closed', { name }), {
        type: 'info',
      });
    }
  },

  // Re-fetch the active folder from the API and update the store.
  refreshFolder: async () => {
    const id = get().activeFolder?.id;
    if (!id) {
      return null;
    }

    set({ isFolderRefreshing: true }, false, 'refreshFolder/start');
    try {
      const response = await getFolder({ id });
      const folder = response?.data ?? null;
      set(
        { activeFolder: folder, isFolderRefreshing: false },
        false,
        'refreshFolder/success',
      );
      return folder;
    } catch (error) {
      set({ isFolderRefreshing: false }, false, 'refreshFolder/error');
      return null;
    }
  },
});
